/**
 * API error model. Legacy main.js throws bare Errors with ad-hoc messages;
 * the React port uses `ApiError` so callers can branch on status / code.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;

  constructor(message: string, status: number, code: string | null = null, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Auth failed / token expired. Caller should clear token + open AuthSheet. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** User isn't allowed to do this action (e.g. delete someone else's post). */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Validation error or business rule violation. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /** Backend / network error. */
  get isServerError(): boolean {
    return this.status >= 500 || this.status === 0;
  }
}

export type ApiErrorBody = {
  message?: string;
  error?: string;
  code?: string;
  details?: unknown;
};

export async function toApiError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = undefined;
  }
  const message =
    body?.message ?? body?.error ?? `Request failed: ${res.status} ${res.statusText || ""}`.trim();
  return new ApiError(message, res.status, body?.code ?? null, body?.details);
}
